import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Download, FileText, Loader2, Mail, Plus, RefreshCw,
  Save, ShieldCheck, Trash2, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, apiError, formatDate, RECORD_STATUSES } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

const STUDENT_FIELDS = [
  ["legal_name", "Legal student name"],
  ["date_of_birth", "Date of birth"],
  ["grade_level", "Grade level"],
  ["academic_year", "Academic year"],
  ["reporting_period", "Reporting period"],
  ["residence", "State / residence"],
  ["student_reference", "Student reference number"],
  ["record_status", "Record status (partner)"],
  ["authorizing_guardian", "Authorizing guardian"],
];

const ATTENDANCE_FIELDS = [
  ["scheduled_days", "Scheduled days"],
  ["present_days", "Present days"],
  ["partial_days", "Partial days"],
  ["excused_days", "Excused days"],
  ["absent_days", "Absent days"],
  ["instructional_time", "Instructional time (completed)"],
  ["scheduled_instructional_time", "Scheduled instructional time"],
];

const AUTHORIZATION_FIELDS = [
  ["package_id", "Package ID"],
  ["student_reference", "Student reference"],
  ["date_generated", "Date generated"],
  ["academic_year", "Academic year"],
  ["authorization_id", "Authorization ID"],
  ["authorizing_guardian", "Authorizing guardian"],
  ["electronic_authorization_status", "Electronic authorization status"],
  ["authorization_accepted_at", "Authorization accepted"],
  ["transfer_scope", "Transfer scope"],
  ["revocation_status", "Revocation status"],
  ["integrity_results", "Record integrity results"],
];

const SUBJECT_COLS = [
  ["name", "Subject"], ["course_title", "Course title"], ["code", "Code"],
  ["final_percentage", "Final %"], ["letter_grade", "Letter"], ["assessments", "Assessments"],
  ["lesson_completion", "Lesson completion"], ["q1", "Q1"], ["q2", "Q2"], ["q3", "Q3"],
  ["q4", "Q4"], ["record_status", "Status"],
];

const CURRICULUM_COLS = [
  ["subject", "Subject"], ["scheduled_lessons", "Scheduled"], ["completed_lessons", "Completed"],
  ["completion_percentage", "Completion"], ["units", "Units"], ["assessments", "Assessments"],
  ["status", "Status"],
];

const MONTHLY_COLS = [
  ["month", "Month"], ["scheduled", "Scheduled"], ["present", "Present"], ["partial", "Partial"],
  ["excused", "Excused"], ["absent", "Absent"], ["instructional_time", "Instr. time"],
];

const emptyRow = (cols) => Object.fromEntries(cols.map(([k]) => [k, ""]));

const Section = ({ title, children, action }) => (
  <div className="mb-8 last:mb-0">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

export default function AdminRecordReview() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [record, setRecord] = useState(null);
  const [data, setData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const [familyEmail, setFamilyEmail] = useState("");
  const [correction, setCorrection] = useState(null);

  const hydrate = (payload) => {
    setRecord(payload);
    const e = payload.extracted || {};
    setData({
      student: e.student || {},
      subjects: e.subjects || [],
      attendance: { ...(e.attendance || {}), monthly: e.attendance?.monthly || [], exceptions: e.attendance?.exceptions || [] },
      curriculum: e.curriculum || [],
      authorization: e.authorization || {},
      assessments: e.assessments || [],
    });
    setFamilyEmail(payload.family?.email || "");
    const latest = payload.versions?.length ? payload.versions[payload.versions.length - 1].version : null;
    setActiveVersion((prev) => prev ?? latest);
  };

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get(`/admin/records/${recordId}`);
      hydrate(payload);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [recordId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (record?.extraction_status !== "processing") return;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [record?.extraction_status, load]);

  useEffect(() => {
    if (!record || !activeVersion) return;
    let url;
    api
      .get(`/admin/records/${recordId}/versions/${activeVersion}/download`, { responseType: "blob" })
      .then(({ data: blob }) => {
        url = URL.createObjectURL(blob);
        setPdfUrl(url);
      })
      .catch(() => setPdfUrl(null));
    return () => url && URL.revokeObjectURL(url);
  }, [record, activeVersion, recordId]);

  const activeFilename =
    (record?.versions || []).find((v) => v.version === activeVersion)?.filename || "";
  const isWorkbook = /\.(xlsx|xlsm)$/i.test(activeFilename);

  const downloadVersion = async () => {
    try {
      const { data: blob } = await api.get(
        `/admin/records/${recordId}/versions/${activeVersion}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = activeFilename || "submission";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setSection = (section, key, value) =>
    setData((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));

  const setRow = (section, index, key, value) =>
    setData((prev) => ({
      ...prev,
      [section]: prev[section].map((r, i) => (i === index ? { ...r, [key]: value } : r)),
    }));

  const setMonthly = (index, key, value) =>
    setData((prev) => ({
      ...prev,
      attendance: {
        ...prev.attendance,
        monthly: prev.attendance.monthly.map((r, i) => (i === index ? { ...r, [key]: value } : r)),
      },
    }));

  const saveFields = async (silent = false) => {
    setBusy(true);
    try {
      const flagged = (record.flagged_fields || []).filter((f) => {
        if (f.startsWith("subject:") || f.startsWith("grade:")) return false;
        const [section, key] = f.split(".");
        if (!key) return false;
        return !String(data[section]?.[key] || "").trim();
      });
      const { data: payload } = await api.put(`/admin/records/${recordId}/extracted`, {
        fields: data,
        flagged_fields: flagged,
      });
      hydrate(payload);
      if (!silent) toast.success("Extracted information saved");
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status, correction_note = "") => {
    setBusy(true);
    try {
      const { data: payload } = await api.put(`/admin/records/${recordId}/status`, {
        status, correction_note,
      });
      hydrate(payload);
      toast.success(`Record marked ${status}`);
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if ((record.match_warnings || []).length) {
      const proceed = window.confirm(
        "This package has unresolved match warnings against the ACS student record. Approve anyway?"
      );
      if (!proceed) return;
    }
    const saved = await saveFields(true);
    if (saved) await setStatus("Approved");
  };

  const generate = async () => {
    if (record.family_email_missing) {
      const proceed = window.confirm(
        "This family has no parent/guardian email on file, so the document cannot be emailed to " +
          "them after it is issued. Issue the document anyway?"
      );
      if (!proceed) return;
    }
    setBusy(true);
    try {
      const { data: payload } = await api.post(`/admin/records/${recordId}/generate-document`);
      hydrate(payload);
      toast.success("ACS academic document generated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data: payload } = await api.post(
        `/admin/students/${record.student_id}/upload`, body,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setActiveVersion(null);
      hydrate(payload);
      toast.success("Package uploaded and information extracted");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveFamilyEmail = async () => {
    setBusy(true);
    try {
      const { data: payload } = await api.put(`/admin/records/${recordId}/family-email`, {
        email: familyEmail.trim(),
      });
      hydrate(payload);
      toast.success("Parent/guardian email saved to the family record");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const { data: payload } = await api.post(`/admin/records/${recordId}/notes`, { note });
    hydrate(payload);
    setNote("");
    toast.success("Internal note added");
  };

  const sendDocument = async (index) => {
    setBusy(true);
    try {
      const { data: payload } = await api.post(`/admin/records/${recordId}/documents/${index}/send`);
      hydrate(payload);
      toast.success("Document emailed to the parent/guardian");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openDocument = async (index, filename) => {
    const { data: blob } = await api.get(`/admin/records/${recordId}/documents/${index}/download`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openSupporting = async (index, filename) => {
    try {
      const { data: blob } = await api.get(
        `/admin/records/${recordId}/supporting/${index}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!record || !data)
    return (
      <AppShell nav={ADMIN_NAV} title="Record review">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </AppShell>
    );

  const flagged = record.flagged_fields || [];
  const isFlagged = (key) => flagged.includes(key);

  return (
    <AppShell
      nav={ADMIN_NAV}
      title={record.student?.legal_name || "Record review"}
      subtitle={`${record.partner_name || "No partner"} · ${record.academic_year || "—"}${
        record.package_id ? ` · Package ${record.package_id}` : ""
      }`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={record.status} testId="review-status-badge" />
          <Button variant="ghost" size="sm" className="gap-2" onClick={() => navigate("/admin/records")}>
            <ArrowLeft className="h-4 w-4" /> Records
          </Button>
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          {record.extraction_status === "processing" && (
            <div
              className="flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-800"
              data-testid="extraction-processing-banner"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Reading the uploaded PDF and mapping the academic information… this screen updates
              automatically when extraction finishes.
            </div>
          )}

          {record.extraction_status === "failed" && (
            <div
              className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700"
              data-testid="extraction-failed-banner"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <strong>Automatic reading of this PDF failed.</strong> The original file is stored and
                every field below can be entered manually. Uploading the package again will retry
                extraction.
              </div>
            </div>
          )}

          {(record.match_warnings || []).length > 0 && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700"
              data-testid="match-warning-banner"
            >
              <div className="mb-2 flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" /> This package may not match the selected student
              </div>
              <ul className="ml-6 list-disc space-y-1">
                {record.match_warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {(record.submission_gaps || []).length > 0 && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900"
              data-testid="submission-gaps-banner"
            >
              <div className="mb-2 flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4" /> Incomplete ACS package — partner action
                needed
              </div>
              <p className="mb-2">
                The partner has been emailed this list. You can still review and correct the record
                yourself.
              </p>
              <ul className="ml-6 list-disc space-y-1">
                {record.submission_gaps.map((g, i) => (
                  <li key={i}>{g}</li>
                ))}
              </ul>
            </div>
          )}

          {(record.supporting_files || []).length > 0 && (
            <div
              className="rounded-md border border-slate-200 bg-white px-5 py-4"
              data-testid="supporting-files-panel"
            >
              <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                Supporting records from the partner
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Stored as evidence only — these never change the extracted package data.
              </p>
              <ul className="mt-2 divide-y divide-slate-100">
                {record.supporting_files.map((f) => (
                  <li
                    key={f.index}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                  >
                    <span>
                      {f.filename} <span className="text-xs text-slate-500">· {f.kind}</span>
                    </span>
                    <button
                      type="button"
                      className="text-sm font-medium text-blue-700 underline"
                      onClick={() => openSupporting(f.index, f.filename)}
                      data-testid={`download-supporting-${f.index}`}
                    >
                      Download
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}


          {flagged.length > 0 && (
            <div
              className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800"
              data-testid="flagged-banner"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <strong>{flagged.length} field(s) need admin attention.</strong> Extraction could not
                confidently identify: {flagged.join(", ")}.
              </div>
            </div>
          )}

          {record.family_email_missing && (
            <div
              className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700"
              data-testid="missing-family-email-warning"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <strong>No parent/guardian email on file.</strong> Add one below so the issued
                document can be emailed to the family.
              </div>
            </div>
          )}

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-slate-900">Parent / guardian contact</h2>
              <p className="mt-1 text-xs text-slate-500">
                {record.family?.parent_name || "No family on file"} · used for document delivery
              </p>
              <div className="mt-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[16rem] flex-1 space-y-1.5">
                  <Label className="text-xs text-slate-600">Parent / guardian email</Label>
                  <Input
                    type="email"
                    value={familyEmail}
                    placeholder="parent@example.com"
                    onChange={(e) => setFamilyEmail(e.target.value)}
                    className={record.family_email_missing ? "border-red-300 bg-red-50" : ""}
                    data-testid="family-email-input"
                  />
                </div>
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={busy || !familyEmail.trim() || familyEmail === (record.family?.email || "")}
                  onClick={saveFamilyEmail}
                  data-testid="save-family-email-button"
                >
                  <Save className="h-4 w-4" /> Save email
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Extracted information</h2>
                  <p className="text-xs text-slate-500">
                    Source: {record.extraction_method || "no upload yet"} · every field is editable
                  </p>
                </div>
                <Button size="sm" className="gap-2" onClick={() => saveFields()} disabled={busy} data-testid="save-fields-button">
                  <Save className="h-4 w-4" /> Save
                </Button>
              </div>

              <Section title="Student information">
                <div className="grid gap-4 sm:grid-cols-2">
                  {STUDENT_FIELDS.map(([key, label]) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-xs text-slate-600">
                        {label}
                        {isFlagged(`student.${key}`) && (
                          <span className="ml-2 text-[10px] font-bold uppercase text-amber-600">flagged</span>
                        )}
                      </Label>
                      <Input
                        value={data.student[key] || ""}
                        onChange={(e) => setSection("student", key, e.target.value)}
                        className={isFlagged(`student.${key}`) ? "border-amber-300 bg-amber-50" : ""}
                        data-testid={`field-student-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </Section>

              <Section
                title="Academic summary & quarterly grades"
                action={
                  <Button
                    variant="outline" size="sm" className="gap-1"
                    onClick={() => setData({ ...data, subjects: [...data.subjects, emptyRow(SUBJECT_COLS)] })}
                    data-testid="add-subject-button"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add subject
                  </Button>
                }
              >
                {data.subjects.length === 0 && (
                  <p className="text-sm text-slate-500">No subjects extracted yet.</p>
                )}
                <div className="space-y-4">
                  {data.subjects.map((row, i) => (
                    <div key={i} className="rounded-md border border-slate-200 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-500">Subject {i + 1}</span>
                        <Button
                          variant="ghost" size="sm" className="text-red-600"
                          onClick={() => setData({ ...data, subjects: data.subjects.filter((_, x) => x !== i) })}
                          data-testid={`remove-subject-${i}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-3">
                        {SUBJECT_COLS.map(([key, label]) => (
                          <div key={key} className="space-y-1">
                            <Label className="text-[11px] text-slate-500">{label}</Label>
                            <Input
                              value={row[key] || ""}
                              onChange={(e) => setRow("subjects", i, key, e.target.value)}
                              data-testid={`subject-${key}-${i}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Required core subjects: {(record.settings?.required_subjects || []).join(", ")} ·
                  minimum passing grade {record.settings?.passing_grade}%
                </p>
              </Section>

              <Section title="Attendance">
                <div className="grid gap-4 sm:grid-cols-3">
                  {ATTENDANCE_FIELDS.map(([key, label]) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-xs text-slate-600">
                        {label}
                        {isFlagged(`attendance.${key}`) && (
                          <span className="ml-2 text-[10px] font-bold uppercase text-amber-600">flagged</span>
                        )}
                      </Label>
                      <Input
                        value={data.attendance[key] || ""}
                        onChange={(e) => setSection("attendance", key, e.target.value)}
                        className={isFlagged(`attendance.${key}`) ? "border-amber-300 bg-amber-50" : ""}
                        data-testid={`field-attendance-${key}`}
                      />
                    </div>
                  ))}
                </div>
                {data.attendance.monthly.length > 0 && (
                  <div className="mt-5">
                    <p className="mb-2 text-xs font-semibold text-slate-500">Monthly attendance</p>
                    <div className="space-y-2">
                      {data.attendance.monthly.map((row, i) => (
                        <div key={i} className="grid grid-cols-7 gap-2">
                          {MONTHLY_COLS.map(([key]) => (
                            <Input
                              key={key}
                              value={row[key] || ""}
                              onChange={(e) => setMonthly(i, key, e.target.value)}
                              className="h-8 text-xs"
                              data-testid={`monthly-${key}-${i}`}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {data.attendance.exceptions.length > 0 && (
                  <p className="mt-3 text-xs text-slate-500" data-testid="attendance-exceptions-count">
                    {data.attendance.exceptions.length} dated attendance exception record(s) retained
                    as supporting evidence.
                  </p>
                )}
              </Section>

              <Section
                title="Curriculum completion"
                action={
                  <Button
                    variant="outline" size="sm" className="gap-1"
                    onClick={() => setData({ ...data, curriculum: [...data.curriculum, emptyRow(CURRICULUM_COLS)] })}
                    data-testid="add-curriculum-button"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add row
                  </Button>
                }
              >
                {data.curriculum.length === 0 && (
                  <p className="text-sm text-slate-500">No curriculum completion rows extracted.</p>
                )}
                <div className="space-y-2">
                  {data.curriculum.map((row, i) => (
                    <div key={i} className="grid grid-cols-7 gap-2">
                      {CURRICULUM_COLS.map(([key]) => (
                        <Input
                          key={key}
                          value={row[key] || ""}
                          onChange={(e) => setRow("curriculum", i, key, e.target.value)}
                          className="h-8 text-xs"
                          data-testid={`curriculum-${key}-${i}`}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Record authorization & integrity">
                <div className="grid gap-4 sm:grid-cols-2">
                  {AUTHORIZATION_FIELDS.map(([key, label]) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-xs text-slate-600">
                        {label}
                        {isFlagged(`authorization.${key}`) && (
                          <span className="ml-2 text-[10px] font-bold uppercase text-amber-600">flagged</span>
                        )}
                      </Label>
                      <Input
                        value={data.authorization[key] || ""}
                        onChange={(e) => setSection("authorization", key, e.target.value)}
                        className={isFlagged(`authorization.${key}`) ? "border-amber-300 bg-amber-50" : ""}
                        data-testid={`field-authorization-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Assessment history (supporting data)">
                <p className="mb-3 text-xs text-slate-500" data-testid="assessment-count">
                  {data.assessments.length} assessment record(s) retained from the partner package.
                  This detail is kept for review and does not appear on the ACS report card.
                </p>
                {data.assessments.length > 0 && (
                  <div className="max-h-60 overflow-y-auto rounded-md border border-slate-200">
                    <table className="w-full text-xs" data-testid="assessment-table">
                      <thead className="bg-slate-50 text-left text-slate-500">
                        <tr>
                          {["Date", "Term", "Course", "Assessment", "Try", "Points", "Score", "Status"].map((h) => (
                            <th key={h} className="px-2 py-1.5 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.assessments.map((a, i) => (
                          <tr key={i} className="border-t border-slate-100 text-slate-700">
                            <td className="px-2 py-1.5">{a.date || "—"}</td>
                            <td className="px-2 py-1.5">{a.term || "—"}</td>
                            <td className="px-2 py-1.5">{a.course || "—"}</td>
                            <td className="px-2 py-1.5">{a.assessment || "—"}</td>
                            <td className="px-2 py-1.5">{a.attempt || "—"}</td>
                            <td className="px-2 py-1.5">{a.points || "—"}</td>
                            <td className="px-2 py-1.5">{a.percentage || "—"}</td>
                            <td className="px-2 py-1.5">{a.status || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-slate-900">Review decision</h2>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Select
                  value={record.status}
                  onValueChange={(v) => (v === "Needs Correction" ? setCorrection({ note: "" }) : setStatus(v))}
                >
                  <SelectTrigger className="w-52" data-testid="review-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECORD_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  disabled={busy || !record.versions?.length || record.extraction_status === "processing"}
                  onClick={approve}
                  data-testid="approve-record-button"
                >
                  <CheckCircle2 className="h-4 w-4" /> Save &amp; approve
                </Button>
                <Button
                  variant="outline" className="gap-2"
                  disabled={
                    busy ||
                    !["Approved", "Document Issued"].includes(record.status) ||
                    record.extraction_status === "processing"
                  }
                  onClick={generate}
                  data-testid="generate-document-button"
                >
                  {record.documents?.length ? <RefreshCw className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                  {record.documents?.length ? "Regenerate ACS document" : "Generate ACS document"}
                </Button>
              </div>
              {record.approved_at && (
                <p className="mt-4 flex items-center gap-2 text-xs text-emerald-700" data-testid="approved-info">
                  <ShieldCheck className="h-3.5 w-3.5" /> Approved annual record saved by{" "}
                  {record.approved_by} on {formatDate(record.approved_at)}
                </p>
              )}
              {record.correction_note && (
                <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="correction-note-display">
                  Correction note to partner: {record.correction_note}
                </p>
              )}

              {record.documents?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                    Issued ACS documents
                  </h3>
                  <ul className="mt-3 divide-y divide-slate-100" data-testid="issued-documents">
                    {record.documents.map((d, i) => (
                      <li key={d.doc_number} className="py-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <span className="font-medium text-slate-900">{d.doc_number}</span>
                            <div className="text-xs text-slate-500">
                              {d.title || "Annual Academic Record & Report Card"}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline" size="sm" className="gap-2"
                              onClick={() => openDocument(i, d.filename)}
                              data-testid={`download-document-${i}`}
                            >
                              <Download className="h-3.5 w-3.5" /> Download
                            </Button>
                            <Button
                              variant="outline" size="sm" className="gap-2"
                              disabled={busy || record.family_email_missing}
                              onClick={() => sendDocument(i)}
                              data-testid={`send-document-${i}`}
                            >
                              <Mail className="h-3.5 w-3.5" />
                              {d.delivered_at ? "Resend email" : "Email to family"}
                            </Button>
                          </div>
                        </div>
                        <div className="mt-1 text-xs text-slate-500" data-testid={`delivery-info-${i}`}>
                          Issued {formatDate(d.issued_at)} ·{" "}
                          {d.delivered_at
                            ? `emailed to ${d.delivered_to} on ${formatDate(d.delivered_at)}${
                                d.delivery_count > 1 ? ` (${d.delivery_count} sends)` : ""
                              }`
                            : "not yet emailed to the family"}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                  Internal admin notes
                </h3>
                <ul className="space-y-2" data-testid="admin-notes">
                  {(record.admin_notes || []).map((n, i) => (
                    <li key={i} className="rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      {n.note}
                      <div className="mt-1 text-xs text-slate-400">{n.by} · {formatDate(n.at)}</div>
                    </li>
                  ))}
                </ul>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add an internal note (not visible to the partner)…"
                  data-testid="admin-note-input"
                />
                <Button variant="outline" size="sm" onClick={addNote} data-testid="add-note-button">
                  Add note
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-base font-semibold text-slate-900">Original partner submission</h2>
                <div className="flex items-center gap-2">
                  {record.versions?.length > 0 && (
                    <Select value={String(activeVersion || "")} onValueChange={(v) => setActiveVersion(Number(v))}>
                      <SelectTrigger className="w-36" data-testid="version-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {record.versions.map((v) => (
                          <SelectItem key={v.version} value={String(v.version)}>
                            Version {v.version}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <input
                    ref={fileRef} type="file" accept=".pdf,.xlsx,.xlsm,application/pdf" className="hidden"
                    onChange={(e) => upload(e.target.files?.[0])}
                    data-testid="admin-upload-input"
                  />
                  <Button
                    variant="outline" size="sm" className="gap-2" disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    data-testid="admin-upload-button"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Upload / replace
                  </Button>
                </div>
              </div>
              {isWorkbook ? (
                <div
                  className="flex h-64 flex-col items-center justify-center gap-3 rounded-md border border-dashed border-slate-300 bg-slate-50 px-6 text-center text-sm text-slate-600"
                  data-testid="workbook-notice"
                >
                  <span>
                    This submission is an Excel workbook ({activeFilename}). Its values have
                    already been read into the fields on the left.
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={downloadVersion}
                    data-testid="download-workbook"
                  >
                    <Download className="h-3.5 w-3.5" /> Download workbook
                  </Button>
                </div>
              ) : pdfUrl ? (
                <iframe
                  title="Partner records package"
                  src={pdfUrl}
                  className="h-[38rem] w-full rounded-md border border-slate-200"
                  data-testid="pdf-viewer"
                />
              ) : (
                <div className="flex h-64 items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">
                  No records package uploaded yet.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-slate-900">Upload history</h2>
              <ul className="mt-3 divide-y divide-slate-100" data-testid="version-history">
                {(record.versions || []).length === 0 && (
                  <li className="py-3 text-sm text-slate-500">No uploads yet.</li>
                )}
                {(record.versions || []).map((v) => (
                  <li key={v.version} className="py-3 text-sm">
                    <div className="font-medium text-slate-900">v{v.version} · {v.filename}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {v.uploaded_by} ({v.source}) · {formatDate(v.uploaded_at)}
                      {v.academic_year ? ` · ${v.academic_year}` : ""}
                      {v.package_id ? ` · package ${v.package_id}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={Boolean(correction)} onOpenChange={(o) => !o && setCorrection(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as needing correction</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Correction note for the partner</Label>
            <Textarea
              value={correction?.note || ""}
              onChange={(e) => setCorrection({ note: e.target.value })}
              data-testid="correction-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrection(null)}>Cancel</Button>
            <Button
              disabled={!String(correction?.note || "").trim()}
              onClick={async () => {
                const ok = await setStatus("Needs Correction", correction.note);
                if (ok) setCorrection(null);
              }}
              data-testid="save-correction-button"
            >
              Send to partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
